import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { TOKEN_KEY, UNAUTHORIZED_EVENT } from '../api/config';
import { refreshAccessToken } from '../api/client';
import {
    clearAuthStorage,
    getStoredRefreshToken,
    getStoredUser,
    hasStoredSession,
    persistAuthResponse,
} from '../api/session';
import * as api from '../api';

const AuthContext = createContext(null);

function isAdminUser(user) {
    return Boolean(user?.admin);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const clearSession = useCallback(() => {
        clearAuthStorage();
        setToken(null);
        setUser(null);
    }, []);

    useEffect(() => {
        let cancelled = false;

        const restoreSession = async () => {
            if (!hasStoredSession()) {
                if (!cancelled) setIsLoading(false);
                return;
            }

            let parsedUser = getStoredUser();

            if (!parsedUser || !isAdminUser(parsedUser)) {
                clearSession();
                if (!cancelled) setIsLoading(false);
                return;
            }

            let storedToken = localStorage.getItem(TOKEN_KEY);

            if (!storedToken && getStoredRefreshToken()) {
                try {
                    const data = await refreshAccessToken();
                    storedToken = data.token;
                    if (data.user) {
                        parsedUser = data.user;
                    }
                } catch {
                    clearSession();
                    if (!cancelled) setIsLoading(false);
                    return;
                }
            }

            if (!storedToken || !isAdminUser(parsedUser)) {
                clearSession();
                if (!cancelled) setIsLoading(false);
                return;
            }

            if (!cancelled) {
                setToken(storedToken);
                setUser(parsedUser);
            }

            try {
                await api.getDashboard(7);
            } catch {
                // 401/403 handled by interceptor
            }

            if (!cancelled) setIsLoading(false);
        };

        restoreSession();

        return () => {
            cancelled = true;
        };
    }, [clearSession]);

    useEffect(() => {
        const handleUnauthorized = () => {
            clearSession();
        };

        window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
        return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    }, [clearSession]);

    const login = useCallback(async (loginValue, password) => {
        const data = await api.adminLogin(loginValue, password);
        if (!isAdminUser(data.user)) {
            throw new Error('Нет прав администратора');
        }
        persistAuthResponse(data);
        setToken(data.token);
        setUser(data.user);
        return data;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.logout();
        } catch {
            // ignore
        }
        clearSession();
    }, [clearSession]);

    const isAuthenticated = Boolean(token && user && isAdminUser(user));

    return (
        <AuthContext.Provider value={{ user, token, isLoading, isAuthenticated, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
